//* Libraries imports
import { useQuery } from "@tanstack/react-query";

//* Schemas Import
import { paginatedProcess } from "../../../schemas/common/process/process";

//? Schemas de Paginação
import { laravelPaginationMetaSchema } from "../../../schemas/common/paginated-data";

//* Axios Import
import { privateApi } from "../../../services/apis/configs/apiClient";

//* Zod Import
import { z } from "zod";

//? Interface para definir os parâmetros de paginação
type GetAllParams = {
  page?: number;
  perPage?: number;
  search?: string;
}

const processResponseSchema = z.object({
  result: z.boolean(),
  data: z.object({
    process: z.array(paginatedProcess),
    pagination: laravelPaginationMetaSchema,
  }),
});

/**
 * API para processos
 */
const ProcessAPI = {
  /**
   * Obtém todos os processos com suporte a paginação
   * @param params Parâmetros de paginação (página, itens por página, termo de busca)
   * @returns Lista paginada de processos
   */
  getAll: async function (params?: GetAllParams) {
    try {

      const response = await privateApi.get(
        "/student_portal/private/processos/get_processos",
        {
          params: {
            page: params?.page || 1,
            limit: params?.perPage || 10,
            search: params?.search || "",
          },
        }
      );
            
      const validatedResponse = processResponseSchema.parse({
        result: true,
        data: {
          process: response.data.process,
          pagination: response.data.pagination
        }
      });
      
      return {
        data: validatedResponse.data.process,
        meta: validatedResponse.data.pagination,
        success: true
      };
    } catch (error) {
      console.error("Erro ao buscar processos:", error);
      return {
        data: [],
        success: false,
        message: "Não foi possível carregar os processos. Tente novamente mais tarde."
      };
    }
  }
}

/**
 * Hook para obter processos paginados
 * @param page Número da página atual
 * @param perPage Quantidade de itens por página
 * @param search Termo de busca (opcional)
 * @returns Query para processos paginados
 */
export function useGetPaginatedProcess(
  page: number = 1,
  perPage: number = 10,
  search: string = ""
) {
  return useQuery({
    queryKey: ["paginated-processes", page, perPage, search],
    queryFn: async () =>
      await ProcessAPI.getAll({
        page,
        perPage,
        search,
      }),
    retry: 3,
    staleTime: 1000 * 60 * 1,
    gcTime: 1000 * 60 * 10,
  });
}
